import { createAsyncThunk } from '@reduxjs/toolkit';
import { ExportAnalyticsJob } from './ExportAnalyticsJob';
import { RootState } from '..';
import { removeJob, updateJob } from './ExportAnalyticsState';
import { AppAnalyticsSnowflakeService } from '@/pages/AppAnalytics/AppAnalyticsSnowflakeService';
import { FuelEntityType, FuelPortalService } from '@/services';
import { dateUtils } from '@/utils/dateUtils';

const BATCH_SIZE = 100;
const DATE_FORMAT = 'MMM/dd/yyyy';

export const processExportContentAnalyticsJob = createAsyncThunk(
  'exportAnalyticsJobs/processExportContentAnalyticsJob',
  async (job: ExportAnalyticsJob, { dispatch, getState }) => {
    const service = AppAnalyticsSnowflakeService();
    const state = getState() as RootState;
    const params = job.params;
    const range = {
      from: dateUtils.format(params.fromDate, params.timezone, DATE_FORMAT),
      to: dateUtils.format(params.toDate, params.timezone, DATE_FORMAT)
    }

    const updateMessage = (message: string) => {
      job.message = message;
      dispatch(updateJob(job));
    };

    job.progress = 0;
    dispatch(updateJob(job));
    updateMessage(`Requesting Snowflake data...`)

    const data = await service.fetchContentAnalytics(params)
    const total = data.length;
    const map = {};
    const clipIds = [];
    for (const row of data) {
      const key = row.video;
      if (key) {
        clipIds.push(key);
        map[key] = {
          views: row.count,
          watchTime: row.duration,
          averageWatchTime: row.average
        }
      }
    }

    job.jobStatus = 'InProgress';
    job.progress = 20;
    dispatch(updateJob(job));

    // populate all clips data
    updateMessage(`Populating clip data`)
    let csv = 'ID, Title, Type, Series, Season, Duration (seconds), Views, Watch Time (seconds), Average Watch Time (seconds), From (UTC), To (UTC)\n';
    let batch = [];
    for await (const clipId of clipIds) {
      if (batch.length === BATCH_SIZE) {
        const update = await processBatch(batch, map, range);
        csv += update;
        batch = [];

        const progress = (BATCH_SIZE / total * 60) + 20;
        job.progress = progress;
        dispatch(updateJob(job));
      }
      batch.push(clipId);
    }
    const update = await processBatch(batch, map, range);
    csv += update;
    job.progress = 80;
    dispatch(updateJob(job));

    // export data as csv
    updateMessage(`Exporting data as CSV`)
    job.progress = 85;
    dispatch(updateJob(job));
    downloadCSV(csv, `content-analytics-${range.from}-to-${range.to}.csv`);

    // finalize
    clearInterval(job.intervalId);
    job.progress = 100;
    job.jobStatus = 'Completed';
    dispatch(updateJob(job));
    dispatch(removeJob(job));
    return true;
  }
);

// helper fns
function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

async function processBatch(clipIds: string[], map, range) {
  const clips = await FuelPortalService.batchGet(FuelEntityType.Clip, clipIds)
  let update = ''

  for (const clip of clips) {
    const clipId = clip.id;
    let row = '';
    let match = map[clipId]
    if (match) {
      const clipDuration = clip.data?.technicalMetadata?.meta?.duration
      // update csv with clip data
      row += clipId + ','
      row += clip.title + ','
      row += `${clip.type || ''},`
      row += `${clip.series || ''},`
      row += `${clip.season || ''},`
      row += clipDuration + ','
      row += match.views + ','
      row += match.watchTime + ','
      row += match.averageWatchTime + ','
      row += `${range.from},${range.to}\n`;

      update += row;
    }
  }

  return update;
}